import { LoadingButton } from '@mui/lab';
import Alert from '@mui/material/Alert';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  InputAdornment,
  Stack,
  Switch,
  TextField,
} from '@mui/material';
import { useFormik } from 'formik';
import { useSnackbar } from 'notistack';
import { paymentService } from '../../../../api/paymentService';
import { TREASURY_TEST_TOP_UP_BANK_TYPE } from '../../../../constants/treasury';
import { treasuryTestTopUpFormFields as F } from '../../../../forms/fields/formFields/treasuryTestTopUp';
import { treasuryTestTopUpSchema } from '../../../../forms/validationSchema/formSchema/treasuryTestTopUp';

type TreasuryTestTopUpDialogProps = {
  open: boolean;
  companyId: number;
  onClose: () => void;
  /** After a successful top-up, e.g. to re-read the bank balance. */
  onDone?: () => void;
};

/** The form, mounted per open — so every open starts empty and validated. */
const TreasuryTestTopUpForm = ({ companyId, onClose, onDone }: Omit<TreasuryTestTopUpDialogProps, 'open'>) => {
  const { enqueueSnackbar } = useSnackbar();

  const { handleSubmit, values, handleChange, handleBlur, setFieldValue, touched, errors, isValid, isSubmitting } =
    useFormik({
      initialValues: { [F.amount]: '', [F.processNow]: true },
      validationSchema: treasuryTestTopUpSchema,
      validateOnMount: true,
      onSubmit: async (formValues) => {
        try {
          const response = await paymentService.createDummyCompanyStatement({
            companyId,
            amount: Number(formValues[F.amount]),
            bankType: TREASURY_TEST_TOP_UP_BANK_TYPE,
          });
          const statementId = response.data?.statementId;

          if (formValues[F.processNow]) {
            const result = await paymentService.processPendingStatements();
            enqueueSnackbar(
              `Statement ${statementId ?? ''} processed — ${result.data?.processed ?? 0} processed, ${
                result.data?.errors ?? 0
              } errors`,
              { variant: result.data?.errors ? 'warning' : 'success' },
            );
          } else {
            enqueueSnackbar(`Statement ${statementId ?? ''} created — pending until the statements run`, {
              variant: 'success',
            });
          }

          onDone?.();
          onClose();
        } catch (error: any) {
          enqueueSnackbar(error?.message || 'Failed to top up the treasury bank', { variant: 'error' });
        }
      },
    });

  const amountError = (touched[F.amount] && errors[F.amount]) || undefined;

  return (
    <form onSubmit={handleSubmit}>
      <DialogTitle>Test Top-up</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          <Alert severity="warning">
            Testing only — not available in production. Creates a fake bank deposit to the treasury bank.
          </Alert>
          <TextField
            fullWidth
            size="small"
            id={F.amount}
            name={F.amount}
            label="Amount"
            type="number"
            value={values[F.amount]}
            onChange={handleChange}
            onBlur={handleBlur}
            error={Boolean(amountError)}
            helperText={amountError}
            InputProps={{ startAdornment: <InputAdornment position="start">SAR</InputAdornment> }}
          />
          <FormControlLabel
            control={
              <Switch
                id={F.processNow}
                checked={Boolean(values[F.processNow])}
                onChange={(event) => setFieldValue(F.processNow, event.target.checked)}
              />
            }
            label="Process the pending statements now"
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <LoadingButton type="submit" variant="contained" loading={isSubmitting} disabled={!isValid}>
          Top Up
        </LoadingButton>
      </DialogActions>
    </form>
  );
};

/**
 * TESTING ONLY — puts money on the company treasury bank through the real deposit path: a dummy ANB incoming
 * statement carrying the treasury bank's account number, which the payment classifier credits to that bank.
 * madkhol-admin and madkhol-payment both refuse it in PROD.
 */
export const TreasuryTestTopUpDialog = ({ open, ...props }: TreasuryTestTopUpDialogProps) => (
  <Dialog open={open} onClose={props.onClose} maxWidth="xs" fullWidth>
    {open && <TreasuryTestTopUpForm {...props} />}
  </Dialog>
);
